import React, { memo, MouseEvent, useCallback, useEffect } from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableContainer from '@mui/material/TableContainer'
import { Column } from '../../../shared/types/Column'
import { useAppDispatch } from '../../../shared/lib/hooks/useAppDispatch/useAppDispatch'
import { useAppSelector } from '../../../shared/lib/hooks/useAppSelector/useAppSelector'
import { MuiTableHead } from '../../../shared/ui/MuiTableHead/MuiTableHead'
import { selectLicenseId } from '../../License'
import { fetchLicenseUpdates } from '../model/services/fetchLicenseUpdates'
import { LicenseUpdatesTable } from './LicenseUpdatesTable/LicenseUpdatesTable'
import { LicenseUpdatesFilter } from './LicenseUpdateFilter/LicenseUpdateFilter'
import {
	LicenseUpdatesFilterChipList
} from './LicenseUpdateFilter/LicenseUpdatesFilterChipList'
import TablePagination from '@mui/material/TablePagination'
import { selectLimit } from '../model/selectors/selectLimit'
import { selectSortDirection } from '../model/selectors/selectSortDirection'
import { selectPage } from '../model/selectors/selectPage'
import { licenseUpdatesActions } from '../model/slices/licenseUpdatesSlice'
import { selectSort } from '../model/selectors/selectSort'
import { selectTotalCount } from '../model/selectors/selectTotalCount'
import { useParams } from 'react-router-dom'

const updatesColumns: Column[] = [
	{ id: 'updateTime', label: 'Update Time', align: 'right', sortable: true },
	{ id: 'manager', label: 'Manager' },
	{ id: 'expirationDate', label: 'Expiration date', align: 'right' },
	{ id: 'type', label: 'Type' },
	{ id: 'state', label: 'State' },
	{ id: 'maxLiveInstances', label: 'Max live instances', align: 'right' },
	{ id: 'parameter', label: 'Preset / Parameter - Value' },
	{ id: 'updateReason', label: 'Update reason' }
]

export const LicenseUpdatesUi = memo(
	function LicenseUpdatesUi() {
		const dispatch = useAppDispatch()
		const licenseIdFromParams = useParams().licenseId
		const licenseId = useAppSelector(selectLicenseId)
		const totalCount = useAppSelector(selectTotalCount)
		const currentPage = useAppSelector(selectPage)
		const limit = useAppSelector(selectLimit)
		const direction = useAppSelector(selectSortDirection)
		const sort = useAppSelector(selectSort)

		const handleChangePage = (_event: unknown, newPage: number) => {
			dispatch(licenseUpdatesActions.setPage(newPage + 1))
		}

		const handleChangeRowsPerPage = (
			event: React.ChangeEvent<HTMLInputElement>
		) => {
			dispatch(licenseUpdatesActions.setLimit(+event.target.value))
			dispatch(licenseUpdatesActions.setPage(1))
		}

		const handleSort = useCallback((
			event: MouseEvent<unknown>,
			property: any
		) => {
			event.preventDefault()
			const isAsc = sort === property && direction === 'ascending'
			dispatch(licenseUpdatesActions.setDirection(isAsc ? 'descending' : 'ascending'))
			dispatch(licenseUpdatesActions.setSort(property))
			dispatch(licenseUpdatesActions.resetPage())
		}, [dispatch, sort, direction])

		useEffect(() => {
			if (licenseIdFromParams) {
				dispatch(fetchLicenseUpdates(licenseIdFromParams))
			}
		}, [dispatch, licenseIdFromParams])

		return (
			<>
				<LicenseUpdatesFilter />
				<LicenseUpdatesFilterChipList licenseId={licenseId} />
				{totalCount ? (
					<>
						<TableContainer sx={{ maxHeight: window.innerHeight * 0.44 }}>
							<Table aria-label="sticky table">
								<MuiTableHead
									flag={false}
									orderBy={sort}
									columns={updatesColumns}
									onRequestSort={handleSort}
									order={direction === 'descending' ? 'desc' : 'asc'}
								/>
								<LicenseUpdatesTable columns={updatesColumns} />
							</Table>
						</TableContainer>
						<TablePagination
							component="div"
							rowsPerPage={+limit}
							rowsPerPageOptions={[10, 25, 50]}
							count={totalCount}
							page={currentPage === 0 ? 0 : currentPage - 1}
							onRowsPerPageChange={handleChangeRowsPerPage}
							onPageChange={handleChangePage}
						/>
					</>
				) : (
					<Box
						sx={{
							p: '2%',
							opacity: '0.7',
							fontSize: '1.2rem',
							textAlign: 'center',
							borderRadius: '8px',
							backgroundColor: '#0000000A'
						}}
					>
						<i>No updates to display</i>
					</Box>
				)}
			</>
		)
	})
